/**
 * @fileoverview "Take or upload a photo" (D78 P1, P2, P3): the photo picker (camera, this device, Google Photos;
 * several at once), the photos put on the tray one at a time underneath (useTrayBatch, the Photos page's own sending),
 * and "Check and save" (CheckAndSave) opened at once over the page, showing this batch's piles as they land and as
 * Grow names them. The server says what each photo is: a plant, seed packets, several kinds, a plant Grow does not
 * have (the tray's guess and packet reader, unchanged).
 *
 * From a bed (its "Add a new plant here"), the photos carry the bed as the tray's hint, so each card's bed is that
 * bed. From Beds' "Add a bed", the cards start on "A new bed…" (newBed). Nothing asks "Photos from" first.
 *
 * One batch at a time: while one is going in, "Take or upload a photo" shows its sheet again rather than the picker,
 * and a pick that arrives meanwhile is not sent.
 *
 * Closing "Check and save" after photos were added reads the Today brief again (B78), so photos left on the tray show as Today's "Sort the N photos
 * waiting" line at once: on the start doors for a garden with no plant yet, and on a started garden's Today.
 *
 * On Free the picker opens as for anyone (no wall, P6): nothing is read, and the sheet says so once.
 *
 * An exact copy of a photo already added is skipped by the server (B61): "Already added, 1 copy skipped" with Undo,
 * said once for the batch by useTrayBatch; a copy added back by Undo joins this batch (onLanded). A Google Photos pick
 * sends Google's item id with each photo, and one that comes back after the picker closed is the tray's (kind 'tray',
 * GooglePhotosResume).
 *
 * Where: useAddPlant (Plants, Seasons, a bed's "Add a new plant here"), useAddBed (Beds), useDoorPhotos (Today's
 * photo door, "A photo of anything", B96).
 * API: POST /tray (useTrayBatch); CheckAndSave's (GET /tray, only while the sheet is open: the hook and the closed
 * sheet stay mounted on the page and read nothing, W24 Job C).
 * Words (en/es): add.photo (the picker's title); CheckAndSave's; photoCopies.* (useTrayBatch).
 *
 * @module usePhotoAdd
 */

import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { trayChanged } from '@/shared/lib/briefFreshness';
import { PhotoDialogShell } from '@/features/plant-list/components/dialogs/photo/PhotoDialogShell';
import { PhotoSourcePicker, type ChosenPicture } from '@/features/plant-list/components/dialogs/photo/PhotoSourcePicker';
import { useCamera } from '@/features/plant-list/components/dialogs/photo/useCamera';
import { useGooglePhotos } from '@/features/plant-list/components/dialogs/photo/useGooglePhotos';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useTrayBatch } from '@/features/photo-tray/lib/useTrayBatch';
import type { Filed } from '@/features/photo-tray/components/TrayPiles';
import { CheckAndSave } from './CheckAndSave';

/** How the photo path is started. */
export interface PhotoAddOptions {
  /** The bed it was started from, sent as each photo's bed; null for none (the cards ask). */
  locationId?: number | null;
  /** Started from Beds' "Add a bed": the cards start on "A new bed…". */
  newBed?: boolean;
  /** Told of every filing (a page leads with the plants just added). */
  onFiled?: (filed: Filed) => void;
}

/** What the hook hands back. */
export interface PhotoAddControl {
  /** Opens the photo picker. */
  start: () => void;
  /** The picker and the sheet: render it once. */
  element: React.ReactNode;
}

/**
 * The photo path of an add pop-up.
 *
 * @param options - PhotoAddOptions.
 * @returns start(), and the element to render once.
 */
export function usePhotoAdd({ locationId = null, newBed = false, onFiled }: PhotoAddOptions = {}): PhotoAddControl {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  /** True while the photo source screen is open. */
  const [choosing, setChoosing] = React.useState(false);
  /** True while "Check and save" is up. */
  const [reviewing, setReviewing] = React.useState(false);
  /** This batch's photos, by tray id, as they land. */
  const [batch, setBatch] = React.useState<ReadonlySet<number>>(() => new Set());
  /** True while this batch's photos are still going in. */
  const [uploading, setUploading] = React.useState(false);
  const camera = useCamera();
  // Kept with a Google Photos pick, so its photos still reach the tray if the screen closes first.
  const google = useGooglePhotos(choosing, { kind: 'tray', locationId });
  const sending = useTrayBatch(locationId, {
    onLanded: (photo) => setBatch((current) => new Set([...current, photo.tray_id])),
    onUploadingChange: setUploading,
  });
  // The camera goes off with the dialog, whichever way it was closed.
  const { stop: stopCamera } = camera;
  React.useEffect(() => {
    if (!choosing) stopCamera();
  }, [choosing, stopCamera]);

  /**
   * Photos chosen: a new batch, "Check and save" up at once, and the photos sent.
   *
   * @param pictures - The pictures, from whichever source (Google's with their item id).
   */
  const picked = (pictures: ChosenPicture[]): void => {
    setChoosing(false);
    // One batch at a time: a second one would reset this batch while its photos still land (review, D78).
    if (sending.busy || pictures.length === 0) return;
    setBatch(new Set());
    setReviewing(true);
    void sending.send(pictures);
  };
  /**
   * Closes the sheet; the unfiled photos stay on the tray. When this batch put photos on the tray, the Today brief is read again (B78), so "Sort the N photos waiting"
   * shows at once for the photos left on the tray, on the start doors and on a started garden's Today alike.
   */
  const close = React.useCallback(() => {
    setReviewing(false);
    if (batch.size > 0) trayChanged(queryClient);
  }, [queryClient, batch]);

  const element = (
    <>
      <PhotoDialogShell isOpen={choosing} onClose={() => setChoosing(false)} title={t('add.photo')}>
        <PhotoSourcePicker
          camera={camera}
          onCaptured={(file) => picked([{ file, takenAt: new Date() }])}
          onFileChosen={(file, takenAt, googleItemId) => picked([{ file, takenAt, googleItemId }])}
          onFilesChosen={picked}
          onPickGoogle={google.pick}
          isPickingGoogle={google.isPicking}
          googleStatus={google.status}
          googleCount={google.count}
          onCancelGoogle={google.cancel}
          googleError={google.error}
        />
      </PhotoDialogShell>
      <CheckAndSave
        open={reviewing}
        onClose={close}
        batch={batch}
        uploading={uploading}
        progress={sending.progress}
        newBed={newBed}
        onFiled={onFiled}
      />
    </>
  );

  /** Opens the picker; while a batch is still going in, the sheet showing it instead. */
  const start = (): void => {
    if (sending.busy) { setReviewing(true); return; }
    setChoosing(true);
  };

  return { start, element };
}
